import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const CalendarManager = () => {
  const [adventures, setAdventures] = useState([]);
  const [selectedAdventure, setSelectedAdventure] = useState('');
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAvailability, setNewAvailability] = useState({
    date: '',
    available_spots: 6,
    price_override: '',
    notes: '',
    is_available: true,
    weather_dependent: true
  });

  useEffect(() => {
    fetchAdventures();
  }, []);

  useEffect(() => {
    if (selectedAdventure) {
      fetchAvailability();
    }
  }, [selectedAdventure]);

  const fetchAdventures = async () => {
    try {
      // Fetch from professional adventures API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/adventures`);

      if (response.ok) {
        const data = await response.json();
        setAdventures(data);
        if (data.length > 0) {
          setSelectedAdventure(data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load adventures:', err);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedAdventure) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/availability/${selectedAdventure}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailabilityData(data.dates || []);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    } finally {
      setLoading(false);
    }
  };

  const createAvailability = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const data = {
        ...newAvailability,
        adventure_id: selectedAdventure,
        price_override: newAvailability.price_override ? parseFloat(newAvailability.price_override) : null
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setNewAvailability({
          date: '',
          available_spots: 6,
          price_override: '',
          notes: '',
          is_available: true,
          weather_dependent: true
        });
        setShowAddForm(false);
        fetchAvailability();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to create availability');
      }
    } catch (err) {
      console.error('Failed to create availability:', err);
      alert('Failed to create availability');
    }
  };

  const updateAvailability = async (availabilityId, updates) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/availability/${availabilityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        fetchAvailability();
      }
    } catch (err) {
      console.error('Failed to update availability:', err);
    }
  };

  const deleteAvailability = async (availabilityId) => {
    if (!window.confirm('Are you sure you want to delete this availability?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/availability/${availabilityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchAvailability();
      }
    } catch (err) {
      console.error('Failed to delete availability:', err);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDate = (date) => {
    // Format date to YYYY-MM-DD in local timezone (Australia/Sydney)
    // This prevents the UTC conversion issue that shifts dates by a day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getAvailabilityForDate = (date) => {
    if (!date) return null;
    const dateStr = formatDate(date);
    return availabilityData.find(avail => avail.date === dateStr);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const selectedAdventureData = adventures.find(adv => adv.id === selectedAdventure);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Adventure Calendar & Availability</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : 'Add Availability'}
        </button>
      </div>

      {/* Adventure Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">Select Adventure</h3>
        <select
          value={selectedAdventure}
          onChange={(e) => setSelectedAdventure(e.target.value)}
          className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Choose an adventure...</option>
          {adventures.map(adventure => (
            <option key={adventure.id} value={adventure.id}>
              {adventure.title}
            </option>
          ))}
        </select>
        
        {selectedAdventureData && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900">{selectedAdventureData.title}</h4>
            <p className="text-sm text-blue-800 mt-1">{selectedAdventureData.short_description}</p>
            <div className="text-sm text-blue-700 mt-2">
              <span className="font-medium">Default Price:</span> ${selectedAdventureData.base_price} •
              <span className="font-medium ml-2">Duration:</span> {selectedAdventureData.duration} •
              <span className="font-medium ml-2">Group Size:</span> {selectedAdventureData.group_size_min}-{selectedAdventureData.group_size_max} people
            </div>
          </div>
        )}
      </div>

      {/* Add Availability Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold mb-4">Add New Availability</h3>
          <form onSubmit={createAvailability} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  value={newAvailability.date}
                  onChange={(e) => setNewAvailability({...newAvailability, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  min={formatDate(new Date())}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Spots
                </label>
                <input
                  type="number"
                  value={newAvailability.available_spots}
                  onChange={(e) => setNewAvailability({...newAvailability, available_spots: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                  max="20"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Override (optional)
                </label>
                <input
                  type="number"
                  value={newAvailability.price_override}
                  onChange={(e) => setNewAvailability({...newAvailability, price_override: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={selectedAdventureData ? `Default: $${selectedAdventureData.base_price}` : ''}
                  step="0.01"
                />
              </div>
              <div className="flex items-center space-x-6 pt-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_available"
                    checked={newAvailability.is_available}
                    onChange={(e) => setNewAvailability({...newAvailability, is_available: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="is_available" className="ml-2 text-sm text-gray-700">
                    Available for booking
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="weather_dependent"
                    checked={newAvailability.weather_dependent}
                    onChange={(e) => setNewAvailability({...newAvailability, weather_dependent: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="weather_dependent" className="ml-2 text-sm text-gray-700">
                    Weather dependent
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={newAvailability.notes}
                onChange={(e) => setNewAvailability({...newAvailability, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
                placeholder="Optional notes about this availability..."
              />
            </div>
            
            <button
              type="submit"
              disabled={!selectedAdventure}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Create Availability
            </button>
          </form>
        </motion.div>
      )}

      {/* Calendar Display */}
      {selectedAdventure && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">
              Availability Calendar - {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {/* Week day headers */}
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {getDaysInMonth(currentMonth).map((date, index) => {
              const availability = getAvailabilityForDate(date);
              const isToday = date && formatDate(date) === formatDate(new Date());
              const isPast = date && date < new Date(new Date().toDateString());
              
              return (
                <div
                  key={index}
                  className={`min-h-[80px] border rounded-lg p-1 ${
                    date ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                >
                  {date && (
                    <div className="h-full flex flex-col">
                      <div className={`text-sm ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                        {date.getDate()}
                      </div>
                      
                      {availability && (
                        <div className="flex-1 mt-1">
                          <div className={`text-xs px-1 py-0.5 rounded ${
                            availability.is_available
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {availability.is_available 
                              ? `${availability.available_spots - availability.booked_spots}/${availability.available_spots} spots`
                              : 'Unavailable'
                            }
                          </div>
                          
                          {availability.price_override && (
                            <div className="text-xs text-blue-600 mt-1">
                              ${availability.price_override}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-1">
                            <button
                              onClick={() => {
                                const spots = prompt('Update available spots:', availability.available_spots);
                                if (spots && !isNaN(spots)) {
                                  updateAvailability(availability.id, { available_spots: parseInt(spots) });
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteAvailability(availability.id)}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Del
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Availability List */}
      {selectedAdventure && availabilityData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">Upcoming Availability</h3>
          <div className="space-y-3">
            {availabilityData
              .filter(avail => new Date(avail.date + 'T00:00:00') >= new Date(new Date().toDateString()))
              .slice(0, 10)
              .map(availability => (
                <div key={availability.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">
                      {new Date(availability.date + 'T00:00:00').toLocaleDateString('en-AU', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        timeZone: 'Australia/Sydney'
                      })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {availability.available_spots - availability.booked_spots} of {availability.available_spots} spots available
                      {availability.price_override && (
                        <span className="ml-2 text-blue-600">${availability.price_override}</span>
                      )}
                    </div>
                    {availability.notes && (
                      <div className="text-sm text-gray-500 mt-1">{availability.notes}</div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded ${
                      availability.is_available
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {availability.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarManager;